import type {
    ActiveMediaDevices,
    MediaDevice,
    MediaDeviceQuery,
    MediaDevicesChangedEvent,
    MediaSessionConfig,
    RequestedMediaDevices,
    SelectedMediaDevices,
} from '@yandex-360/telemost-sdk/device-manager';
import type {
    ActiveDevices,
    Device,
    DeviceOrQuery,
    DevicesChangedEvent,
    RequestedDevices,
    SessionConfig,
    SessionEvent,
    SoundProcessorOptions,
    VideoProcessorOptions,
} from '@yandex-video-platform/goloom-sdk';

export const mapGoloomDeviceFromGoloom = (device: Device): MediaDevice => {
    const base: MediaDevice = {
        kind: device.kind,
        id: device.id,
        label: device.label,
        group: device.group,
    };

    if (device.kind === 'audioinput' || device.kind === 'videoinput') {
        return {
            ...base,
            track: device.track ?? undefined,
            constraints: device.constraints,
        };
    }

    return {
        ...base,
        constraints: device.constraints,
    };
};

const isGoloomDevice = (device: Device | DeviceOrQuery): device is Device =>
    'id' in device && device.id !== undefined && 'label' in device && device.label !== undefined;

export const mapDeviceFromGoloom = (device: Device | DeviceOrQuery): MediaDevice | MediaDeviceQuery => {
    if (isGoloomDevice(device)) {
        return mapGoloomDeviceFromGoloom(device);
    }

    return {
        kind: device.kind,
        ...('id' in device && typeof device.id === 'string' ? { id: device.id } : {}),
        constraints: 'constraints' in device ? device.constraints : undefined,
    };
};

export const mapDeviceToGoloom = (device: MediaDevice | MediaDeviceQuery): DeviceOrQuery => {
    return device as DeviceOrQuery;
};

export const mapActiveDevicesFromGoloom = (activeDevices: ActiveDevices): ActiveMediaDevices => ({
    audioInput: activeDevices.audioInput ? mapDeviceFromGoloom(activeDevices.audioInput) : null,
    videoInput: activeDevices.videoInput ? mapDeviceFromGoloom(activeDevices.videoInput) : null,
    audioOutput: activeDevices.audioOutput ? mapDeviceFromGoloom(activeDevices.audioOutput) : null,
});

export const mapSelectedDevicesFromGoloom = (selectedDevices: {
    audioInput: DeviceOrQuery | null;
    videoInput: DeviceOrQuery | null;
    audioOutput: DeviceOrQuery | null;
}): SelectedMediaDevices => ({
    audioInput: selectedDevices.audioInput ? mapDeviceFromGoloom(selectedDevices.audioInput) : null,
    videoInput: selectedDevices.videoInput ? mapDeviceFromGoloom(selectedDevices.videoInput) : null,
    audioOutput: selectedDevices.audioOutput ? mapDeviceFromGoloom(selectedDevices.audioOutput) : null,
});

export const mapRequestedDevicesFromGoloom = (devices: RequestedDevices): RequestedMediaDevices => ({
    audioInput: devices.audioInput.map(mapGoloomDeviceFromGoloom),
    videoInput: devices.videoInput.map(mapGoloomDeviceFromGoloom),
    audioOutput: devices.audioOutput.map(mapGoloomDeviceFromGoloom),
});

export const mapDeviceChangedFromGoloom = (event: DevicesChangedEvent): MediaDevicesChangedEvent => ({
    added: event.added.map(mapGoloomDeviceFromGoloom),
    removed: event.removed.map(mapGoloomDeviceFromGoloom),
});

export const mapActiveDevicesChangedFromGoloom = (
    event: SessionEvent['activeDevicesChanged']
): { activeDevices: ActiveMediaDevices } => ({
    activeDevices: mapActiveDevicesFromGoloom(event.activeDevices),
});

export const mapSessionConfigToGoloom = (config: MediaSessionConfig): SessionConfig => ({
    soundProcessing: config.soundProcessing as SessionConfig['soundProcessing'],
    videoProcessing: config.videoProcessing as SessionConfig['videoProcessing'],
    flags: config.flags as SessionConfig['flags'],
});

export const mapSoundProcessorToGoloom = (options: Record<string, unknown>): SoundProcessorOptions =>
    options as unknown as SoundProcessorOptions;

export const mapVideoProcessorToGoloom = (options: Record<string, unknown>): VideoProcessorOptions =>
    options as unknown as VideoProcessorOptions;
